'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { gatewayClient } from '@/src/api';
import { ReferralProgram } from '@/src/types/referral';
import RewardTermsLayout, { RewardTermsSection } from './RewardTermsLayout';
import styles from '../StaticPage.module.css';

// Order must match the `sections` array in the locale JSON, which supplies the
// TOC labels for these entries by index.
const SECTIONS: Omit<RewardTermsSection, 'slot'>[] = [
  { id: 'overview', contentKey: 'overview' },
  { id: 'your-link', contentKey: 'yourLink' },
  { id: 'who-counts', contentKey: 'whoCounts' },
  { id: 'one-order-rule', contentKey: 'oneOrderRule' },
  { id: 'calculation', contentKey: 'calculation' },
  { id: 'current-values', contentKey: 'currentValues' },
  { id: 'crediting', contentKey: 'crediting' },
  { id: 'promoting', contentKey: 'promoting' },
  { id: 'fair-use', contentKey: 'fairUse' },
  { id: 'cancellation', contentKey: 'cancellation' },
  { id: 'changes', contentKey: 'changes' },
  { id: 'taxes', contentKey: 'taxes' },
  { id: 'contact', contentKey: 'contact' },
];

const formatEur = (cents: number) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

// The rate may be sub-percent (0.33), so don't round to whole numbers.
const formatPercent = (rate: number) =>
  `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(rate)}%`;

const ReferralTerms: React.FC = () => {
  const t = useTranslations('referralTerms');

  const [program, setProgram] = useState<ReferralProgram | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    gatewayClient.referrals
      .getProgram()
      .then((data) => {
        if (!cancelled) setProgram(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Values are admin-tunable at runtime, so they are rendered from the API and
  // never written into the legal copy.
  const valuesSlot = (
    <>
      {failed && <p className={styles.contentText}>{t('values.unavailable')}</p>}

      {program && !program.enabled && (
        <p className={styles.contentText}>{t('values.suspended')}</p>
      )}

      {program && program.enabled && (
        <table className={styles.dataTable}>
          <caption className={styles.contentText}>{t('values.title')}</caption>
          <tbody>
            <tr>
              <th scope="row">{t('values.rateLabel')}</th>
              <td>{formatPercent(program.ratePercent)}</td>
            </tr>
            <tr>
              <th scope="row">{t('values.minOrderLabel')}</th>
              <td>{formatEur(program.minOrderEurCents)}</td>
            </tr>
            <tr>
              <th scope="row">{t('values.holdingPeriodLabel')}</th>
              <td>
                {program.maturityDays} {t('values.daysLabel')}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <p className={styles.contentText}>{t('content.currentValues.note')}</p>
    </>
  );

  const sections: RewardTermsSection[] = SECTIONS.map((section) =>
    section.id === 'current-values' ? { ...section, slot: valuesSlot } : section,
  );

  return (
    <RewardTermsLayout
      namespace="referralTerms"
      breadcrumbKey="referralTerms"
      canonicalPath="/referral-terms"
      sections={sections}
      related={[
        { labelKey: 'relatedCashback', canonicalPath: '/cashback-terms' },
        { labelKey: 'relatedTerms', canonicalPath: '/terms' },
      ]}
    />
  );
};

export default ReferralTerms;
